import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  buildShopImagePath,
  supabaseConfigured,
  uploadImage,
} from "../supabaseClient.js";
import {
  fetchShop,
  updateShop,
  updateHairdresser,
  createAppointmentType,
  updateAppointmentType,
} from "../api.js";
import { ImageCropper } from "../components/ImageCropper";
import { Button } from "../components/ui/button.jsx";
import { ArrowLeft } from "lucide-react";

export default function ShopConfigPage() {
  const { orgSlug, shopId } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // shop detail form
  const [shopForm, setShopForm] = useState({
    name: "", timezone: "", address: "", contact_email: "", contact_phone: "", min_advance_hours: 0,
    image_url: null,
  });
  const [shopSaving, setShopSaving] = useState(false);
  const [shopImageFile, setShopImageFile] = useState(null);
  const [shopImagePreview, setShopImagePreview] = useState(null);
  const [shopImageUploading, setShopImageUploading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  // inline editing appointment type
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [editTypeForm, setEditTypeForm] = useState({
    name: "",
    duration_minutes: 15,
    price: 0,
    active: true
  });

  // new appointment type
  const [showNewType, setShowNewType] = useState(false);
  const [newTypeForm, setNewTypeForm] = useState({ name: "", duration_minutes: 15, price: 0 });

  const loadShop = useCallback(async () => {
    try {
      const data = await fetchShop(shopId);
      setShop(data);
      setShopForm({
        name: data.name || "",
        timezone: data.timezone || "",
        address: data.address || "",
        contact_email: data.contact_email || "",
        contact_phone: data.contact_phone || "",
        min_advance_hours: data.min_advance_hours ?? 0,
        image_url: data.image_url || null,
      });
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao carregar salão");
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => { loadShop(); }, [loadShop]);

  useEffect(() => {
    if (!shopImageFile) {
      setShopImagePreview(null);
      return;
    }
    const url = URL.createObjectURL(shopImageFile);
    setShopImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [shopImageFile]);

  const setShopField = (field) => (e) => setShopForm({ ...shopForm, [field]: e.target.value });

  const handleSaveShop = async () => {
    setShopSaving(true);
    setError(null);
    try {
      let imageUrl = shopForm.image_url || null;
      if (shopImageFile) {
        setShopImageUploading(true);
        const path = buildShopImagePath(shopId, shopImageFile);
        imageUrl = await uploadImage({ file: shopImageFile, path });
      }
      await updateShop(shopId, { ...shopForm, image_url: imageUrl });
      setShopImageFile(null);
      await loadShop();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Erro ao guardar");
    } finally {
      setShopImageUploading(false);
      setShopSaving(false);
    }
  };

  const handleToggleShopActive = async () => {
    const nextActive = !shop.active;
    const message = nextActive
      ? "Tem a certeza que quer ativar este salão?"
      : "Tem a certeza que quer desativar este salão?";
    if (!confirm(message)) return;
    try {
      if (nextActive) {
        await updateShop(shopId, { active: true });
      } else {
        await updateShop(shopId, { active: false });
      }
      await loadShop();
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao atualizar estado do salão");
    }
  };

  const handleToggleHairdresser = async (h) => {
    try {
      await updateHairdresser(shopId, h.id, { active: !h.active });
      await loadShop();
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao atualizar profissional");
    }
  };

  const handleShopImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setShopImageFile(null);
      return;
    }
    setPendingFile(file);
    setShowCropper(true);
    e.target.value = "";
  };

  const handleRemoveShopImage = () => {
    setShopImageFile(null);
    setShopForm({ ...shopForm, image_url: null });
  };

  const handleCropComplete = (croppedFile) => {
    setShopImageFile(croppedFile);
    setPendingFile(null);
    setShowCropper(false);
  };

  const handleCropCancel = () => {
    setPendingFile(null);
    setShowCropper(false);
  };

  const startEditType = (t) => {
    setEditingTypeId(t.id);
    setEditTypeForm({
      name: t.name,
      duration_minutes: t.duration_minutes,
      price: t.price ?? 0,
      active: t.active
    });
  };

  const handleSaveType = async () => {
    try {
      await updateAppointmentType(shopId, editingTypeId, editTypeForm);
      setEditingTypeId(null);
      await loadShop();
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao guardar serviço");
    }
  };

  const handleCreateType = async () => {
    try {
      await createAppointmentType(shopId, newTypeForm);
      setNewTypeForm({ name: "", duration_minutes: 15, price: 0 });
      setShowNewType(false);
      await loadShop();
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao criar serviço");
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-background p-6 md:p-12">
        <p className="text-white text-center">A carregar…</p>
      </main>
    );
  }

  if (!shop) {
    return (
      <main className="min-h-screen bg-background p-6 md:p-12">
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg max-w-2xl mx-auto">
          {error || "Salão não encontrado"}
        </div>
      </main>
    );
  }

  const typeMap = {};
  (shop.appointment_types || []).forEach((t) => { typeMap[t.id] = t.name; });
  const displayShopImage = shopImagePreview || shopForm.image_url;

  return (
    <main className="min-h-screen bg-background p-6 md:p-12">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate(`/admin/${orgSlug}`)}
          className="flex items-center gap-2 text-white mb-6 hover:text-gold transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar</span>
        </button>

        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gold mb-3">
            {shop.name}
          </h1>
        </header>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Shop details */}
        <section className="bg-card border border-border rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold text-white mb-6">Detalhes do salão</h2>
          <div className="space-y-6">
            {/* Image upload */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-white">
                Imagem do salão
              </label>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="w-40 h-40 rounded-lg overflow-hidden border border-border bg-background flex items-center justify-center">
                  {displayShopImage ? (
                    <img src={displayShopImage} alt="Imagem do salão" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-muted text-sm">Sem imagem</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleShopImageChange}
                    disabled={!supabaseConfigured || shopImageUploading}
                    className="text-sm text-muted file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gold file:text-white hover:file:bg-gold-dark file:cursor-pointer disabled:opacity-50"
                  />
                  {shopForm.image_url && (
                    <Button size="sm" variant="outline" onClick={handleRemoveShopImage}>
                      Remover
                    </Button>
                  )}
                  {shopImageUploading && <span className="text-sm text-muted">A enviar…</span>}
                  {!supabaseConfigured && (
                    <p className="text-xs text-muted">Defina VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY para uploads.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Form fields */}
            <div className="space-y-2">
              <label htmlFor="shop-name" className="block text-sm font-medium text-white">Nome</label>
              <input
                id="shop-name"
                value={shopForm.name}
                onChange={setShopField("name")}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="shop-address" className="block text-sm font-medium text-white">Morada</label>
              <input
                id="shop-address"
                value={shopForm.address}
                onChange={setShopField("address")}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="shop-email" className="block text-sm font-medium text-white">Email de contacto</label>
              <input
                id="shop-email"
                value={shopForm.contact_email}
                onChange={setShopField("contact_email")}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="shop-phone" className="block text-sm font-medium text-white">Telefone de contacto</label>
              <input
                id="shop-phone"
                value={shopForm.contact_phone}
                onChange={setShopField("contact_phone")}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="shop-advance" className="block text-sm font-medium text-white">Horas mínimas de antecedência das reservas</label>
              <input
                id="shop-advance"
                type="number"
                min="0"
                value={shopForm.min_advance_hours}
                onChange={(e) => setShopForm({ ...shopForm, min_advance_hours: parseInt(e.target.value, 10) || 0 })}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            <div className="flex gap-3">
              <Button onClick={handleSaveShop} disabled={shopSaving}>
                {shopSaving ? "A guardar…" : "Guardar"}
              </Button>
              <Button
                variant="outline"
                onClick={handleToggleShopActive}
              >
                {shop.active ? "Desativar salão" : "Ativar salão"}
              </Button>
            </div>
          </div>
        </section>

        {/* Hairdressers */}
        <section className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-white">Profissionais</h2>
            <Button onClick={() => navigate(`/admin/${orgSlug}/shops/${shopId}/hairdressers/new`)}>
              Criar profissional
            </Button>
          </div>
          {(shop.hairdressers || []).length === 0 ? (
            <p className="text-muted">Nenhum profissional registado.</p>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-white">Nome</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-white">Estado</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-white">Serviços</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-white">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shop.hairdressers.map((h) => (
                      <tr key={h.id} className="border-b border-border/50">
                        <td className="py-3 px-4 text-white">{h.name}</td>
                        <td className="py-3 px-4">
                          <span
                            className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                              h.active
                                ? "bg-green-500/10 text-green-400"
                                : "bg-red-500/10 text-red-400"
                            }`}
                          >
                            {h.active ? "Ativo" : "Inativo"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-muted text-sm">
                          {(h.appointment_type_ids || []).map((id) => typeMap[id]).filter(Boolean).join(", ") || "—"}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/admin/${orgSlug}/shops/${shopId}/hairdressers/${h.id}`)}
                            >
                              Editar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleHairdresser(h)}
                            >
                              {h.active ? "Desativar" : "Ativar"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="block md:hidden space-y-4">
                {shop.hairdressers.map((h) => (
                  <div key={h.id} className="bg-background border border-border rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold text-white">{h.name}</h3>
                      <span
                        className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                          h.active
                            ? "bg-green-500/10 text-green-400"
                            : "bg-red-500/10 text-red-400"
                        }`}
                      >
                        {h.active ? "Ativo" : "Inativo"}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-muted">Serviços: </span>
                      <span className="text-white">
                        {(h.appointment_type_ids || []).map((id) => typeMap[id]).filter(Boolean).join(", ") || "—"}
                      </span>
                    </div>
                    <div className="flex flex-col gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/admin/${orgSlug}/shops/${shopId}/hairdressers/${h.id}`)}
                        className="w-full"
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleHairdresser(h)}
                        className="w-full"
                      >
                        {h.active ? "Desativar" : "Ativar"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>

        {/* Appointment types */}
        <section className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-white">Serviços</h2>
            <Button onClick={() => setShowNewType(true)}>
              Criar serviço
            </Button>
          </div>
          {(shop.appointment_types || []).length === 0 ? (
            <p className="text-muted">Nenhum serviço registado.</p>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-white">Nome</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-white">Duração (min)</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-white">Preço (€)</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-white">Estado</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-white">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shop.appointment_types.map((t) =>
                      editingTypeId === t.id ? (
                        <tr key={t.id} className="border-b border-border/50">
                          <td className="py-3 px-4">
                            <input
                              value={editTypeForm.name}
                              onChange={(e) => setEditTypeForm({ ...editTypeForm, name: e.target.value })}
                              className="w-full px-3 py-1.5 bg-background border border-border rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                            />
                          </td>
                          <td className="py-3 px-4">
                            <input
                              type="number"
                              value={editTypeForm.duration_minutes}
                              onChange={(e) => setEditTypeForm({ ...editTypeForm, duration_minutes: Number(e.target.value) })}
                              className="w-20 px-3 py-1.5 bg-background border border-border rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                            />
                          </td>
                          <td className="py-3 px-4">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editTypeForm.price}
                              onChange={(e) => setEditTypeForm({ ...editTypeForm, price: Number(e.target.value) })}
                              className="w-24 px-3 py-1.5 bg-background border border-border rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                            />
                          </td>
                          <td className="py-3 px-4">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={editTypeForm.active}
                                onChange={(e) => setEditTypeForm({ ...editTypeForm, active: e.target.checked })}
                                className="rounded border-border bg-background text-gold focus:ring-2 focus:ring-gold"
                              />
                              <span className="text-white text-sm">Ativo</span>
                            </label>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="outline" onClick={handleSaveType}>Guardar</Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingTypeId(null)}>Cancelar</Button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <tr key={t.id} className="border-b border-border/50">
                          <td className="py-3 px-4 text-white">{t.name}</td>
                          <td className="py-3 px-4 text-white">{t.duration_minutes}</td>
                          <td className="py-3 px-4 text-white">{t.price}</td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                                t.active
                                  ? "bg-green-500/10 text-green-400"
                                  : "bg-red-500/10 text-red-400"
                              }`}
                            >
                              {t.active ? "Ativo" : "Inativo"}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex justify-end">
                              <Button size="sm" variant="outline" onClick={() => startEditType(t)}>Editar</Button>
                            </div>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="block md:hidden space-y-4">
                {shop.appointment_types.map((t) =>
                  editingTypeId === t.id ? (
                    <div key={t.id} className="bg-background border border-border rounded-lg p-4 space-y-3">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-white">Nome</label>
                        <input
                          value={editTypeForm.name}
                          onChange={(e) => setEditTypeForm({ ...editTypeForm, name: e.target.value })}
                          className="w-full px-3 py-2 bg-card border border-border rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-white">Duração (min)</label>
                        <input
                          type="number"
                          value={editTypeForm.duration_minutes}
                          onChange={(e) => setEditTypeForm({ ...editTypeForm, duration_minutes: Number(e.target.value) })}
                          className="w-full px-3 py-2 bg-card border border-border rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-white">Preço (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editTypeForm.price}
                          onChange={(e) => setEditTypeForm({ ...editTypeForm, price: Number(e.target.value) })}
                          className="w-full px-3 py-2 bg-card border border-border rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={editTypeForm.active}
                            onChange={(e) => setEditTypeForm({ ...editTypeForm, active: e.target.checked })}
                            className="rounded border-border bg-background text-gold focus:ring-2 focus:ring-gold"
                          />
                          <span className="text-white text-sm">Ativo</span>
                        </label>
                      </div>
                      <div className="flex flex-col gap-2 pt-2">
                        <Button size="sm" variant="outline" onClick={handleSaveType} className="w-full">Guardar</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingTypeId(null)} className="w-full">Cancelar</Button>
                      </div>
                    </div>
                  ) : (
                    <div key={t.id} className="bg-background border border-border rounded-lg p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <h3 className="text-lg font-semibold text-white">{t.name}</h3>
                        <span
                          className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                            t.active
                              ? "bg-green-500/10 text-green-400"
                              : "bg-red-500/10 text-red-400"
                          }`}
                        >
                          {t.active ? "Ativo" : "Inativo"}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div>
                          <span className="text-muted">Duração: </span>
                          <span className="text-white">{t.duration_minutes} min</span>
                        </div>
                        <div>
                          <span className="text-muted">Preço: </span>
                          <span className="text-white">€{t.price}</span>
                        </div>
                      </div>
                      <div className="pt-2">
                        <Button size="sm" variant="outline" onClick={() => startEditType(t)} className="w-full">
                          Editar
                        </Button>
                      </div>
                    </div>
                  )
                )}
              </div>
            </>
          )}
          {showNewType && (
            <div className="mt-6 p-4 bg-background border border-border rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-white">Nome do serviço</label>
                  <input
                    placeholder="Nome do serviço"
                    value={newTypeForm.name}
                    onChange={(e) => setNewTypeForm({ ...newTypeForm, name: e.target.value })}
                    className="w-full px-3 py-2 bg-card border border-border rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-white">Duração (min)</label>
                  <input
                    type="number"
                    placeholder="Duração (min)"
                    value={newTypeForm.duration_minutes}
                    onChange={(e) => setNewTypeForm({ ...newTypeForm, duration_minutes: Number(e.target.value) })}
                    className="w-full px-3 py-2 bg-card border border-border rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-white">Preço (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Preço (€)"
                    value={newTypeForm.price}
                    onChange={(e) => setNewTypeForm({ ...newTypeForm, price: Number(e.target.value) })}
                    className="w-full px-3 py-2 bg-card border border-border rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleCreateType}>Criar</Button>
                <Button size="sm" variant="outline" onClick={() => setShowNewType(false)}>Cancelar</Button>
              </div>
            </div>
          )}
        </section>
      </div>
      {showCropper && pendingFile && (
        <ImageCropper
          file={pendingFile}
          cropType="salon"
          onComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </main>
  );
}
